import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useStore } from '../context/StoreContext';
import { ProductCard } from '../components/ProductCard';
import { TRANSLATIONS } from '../constants';
import { ArrowRight, SearchX, Laptop, ShoppingBag, Home as HomeIcon } from 'lucide-react';
import { updateMetaTags, injectStructuredData, generateOrganizationStructuredData } from '../utils/seo';
import { SEO_CONFIG, getFullUrl, getFullImageUrl } from '../config/seo.config';
import { HomeSkeleton } from '../components/Shimmer';

export const Home = () => {
  const { products, categories, language, searchQuery, searchResults, loading } = useStore();

  useEffect(() => {
    // Update meta tags for home page
    updateMetaTags({
      title: `${SEO_CONFIG.storeName} - Sklep internetowy z zabawkami`,
      description: SEO_CONFIG.storeDescription,
      keywords: SEO_CONFIG.defaultKeywords,
      canonical: getFullUrl('/'),
      ogImage: getFullImageUrl(SEO_CONFIG.ogImage),
      ogType: 'website',
    });

    // Inject organization structured data
    injectStructuredData(generateOrganizationStructuredData());
  }, []);

  // Category icon mapping
  const categoryIcons: Record<string, React.ReactNode> = {
    electronics: <Laptop className="w-8 h-8 md:w-10 md:h-10" />,
    clothing: <ShoppingBag className="w-8 h-8 md:w-10 md:h-10" />,
    home: <HomeIcon className="w-8 h-8 md:w-10 md:h-10" />,
  };

  // Available color schemes for categories
  const colorSchemes = [
    {
      bg: 'bg-blue-50 hover:bg-blue-100 border-blue-200',
      icon: 'text-blue-600'
    },
    {
      bg: 'bg-pink-50 hover:bg-pink-100 border-pink-200',
      icon: 'text-pink-600'
    },
    {
      bg: 'bg-green-50 hover:bg-green-100 border-green-200',
      icon: 'text-green-600'
    },
    {
      bg: 'bg-purple-50 hover:bg-purple-100 border-purple-200',
      icon: 'text-purple-600'
    },
    {
      bg: 'bg-orange-50 hover:bg-orange-100 border-orange-200',
      icon: 'text-orange-600'
    },
    {
      bg: 'bg-teal-50 hover:bg-teal-100 border-teal-200',
      icon: 'text-teal-600'
    },
    {
      bg: 'bg-indigo-50 hover:bg-indigo-100 border-indigo-200',
      icon: 'text-indigo-600'
    },
    {
      bg: 'bg-rose-50 hover:bg-rose-100 border-rose-200',
      icon: 'text-rose-600'
    }
  ];

  // Function to get color scheme based on category index
  const getCategoryColorScheme = (categoryId: string, index: number) => {
    return colorSchemes[index % colorSchemes.length];
  };
  
  if (loading) {
    return <HomeSkeleton />;
  }

  // Display search results when there's a search query
  if (searchQuery) {
    const displayProducts = searchResults.length > 0 ? searchResults : [];
    
    return (
        <div className="space-y-8">
            <h2 className="text-2xl font-bold text-gray-900">
                {language === 'en' ? `Search Results for "${searchQuery}"` : `Wyniki wyszukiwania dla "${searchQuery}"`}
            </h2>
            {displayProducts.length > 0 ? (
                <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
                    {displayProducts.map(product => (
                        <ProductCard key={product.id} product={product} />
                    ))}
                </div>
            ) : (
                <div className="flex flex-col items-center justify-center py-20 text-gray-500">
                    <SearchX className="w-16 h-16 mb-4 text-gray-300" />
                    <p className="text-lg">{language === 'en' ? 'No products found matching your search.' : 'Nie znaleziono produktów pasujących do wyszukiwania.'}</p>
                </div>
            )}
        </div>
    );
  }

  const featuredProducts = products.slice(0, 4);

  return (
    <main className="space-y-8 md:space-y-12">
      {/* Hero Section */}
      <section className="relative rounded-2xl overflow-hidden bg-slate-900 text-white py-12 px-6 md:py-16 md:px-16 flex flex-col items-start justify-center min-h-[350px] md:min-h-[550px]" aria-label="Hero banner">
         <div className="absolute inset-0 opacity-80">
            <img src="dido-zabawki.jpeg" alt="Hero" className="w-full h-full object-cover object-center" />
         </div>
             <div className="relative z-10 max-w-2xl mt-auto">
              <h1 className="text-xl md:text-3xl font-bold mb-4 md:mb-6 leading-tight text-slate-900">
                {language === 'en' ? 'Gdzie znajdziesz fajne zabawki dla dzieci !!!' : 'Gdzie znajdziesz fajne zabawki dla dzieci !!!'}
              </h1>
              <p className="text-base md:text-lg text-slate-800 mb-6 md:mb-8 max-w-lg">
                {language === 'en' 
                ? 'Odkryj naszą kolekcję misiów, puzzli, breloków i innych zabawnych zabawek.' 
                : 'Odkryj naszą kolekcję misiów, puzzli, breloków i innych zabawnych zabawek.'}
              </p>
              <Link 
                to="/categories" 
                className="inline-flex items-center gap-2 bg-emerald-500 hover:bg-emerald-600 text-white font-medium px-5 py-3 md:px-6 md:py-3 rounded-lg transition-colors text-sm md:text-base"
              >
                {language === 'en' ? 'Shop Now' : 'Kup Teraz'}
                <ArrowRight className="w-4 h-4 md:w-5 md:h-5" />
              </Link>
             </div>
      </section>

      {/* Shop by Categories Section */}
      <section aria-labelledby="categories-heading" className="py-4">
        <div className="flex flex-row items-center justify-between mb-6 md:mb-8">
          <h2 id="categories-heading" className="text-xl md:text-2xl font-bold text-gray-900">
            {TRANSLATIONS.shopByCategories[language]}
          </h2>
        </div>
        <div className="flex overflow-x-auto gap-4 pb-4 sm:grid sm:grid-cols-3 sm:pb-0 md:gap-6 snap-x snap-mandatory scrollbar-none -mx-4 px-4 sm:mx-0 sm:px-0">
          {categories.map((category, index) => {
            const colorScheme = getCategoryColorScheme(category.id, index);
            return (
              <Link
                key={category.id}
                to={`/categories?cat=${category.id}`}
                className={`group relative overflow-hidden rounded-xl border-2 transition-all duration-300 p-6 md:p-8 flex flex-col items-center justify-center text-center flex-shrink-0 min-w-[70vw] sm:min-w-0 snap-start ${colorScheme.bg}`}
                aria-label={`Browse ${category.name[language]}`}
              >
                <div className={`mb-4 ${colorScheme.icon}`}>
                  {categoryIcons[category.id] || <ShoppingBag className="w-8 h-8 md:w-10 md:h-10" />}
                </div>
              <h3 className="text-lg md:text-xl font-semibold text-gray-900 mb-2">
                {category.name[language]}
              </h3>
              <p className="text-sm text-gray-600 mb-3">
                {language === 'en' 
                  ? `Explore our ${category.name[language].toLowerCase()} collection` 
                  : `Odkryj naszą kolekcję ${category.name[language].toLowerCase()}`}
              </p>
              <span className="inline-flex items-center gap-1 text-sm font-medium text-gray-700 group-hover:gap-2 transition-all">
                {TRANSLATIONS.browseCategory[language]}
                <ArrowRight className="w-4 h-4" />
              </span>
            </Link>
          );
          })}
        </div>
      </section>

      {/* Featured Section */}
      <section aria-labelledby="featured-heading">
        <div className="flex flex-row items-center justify-between mb-6 md:mb-8">
          <h2 id="featured-heading" className="text-xl md:text-2xl font-bold text-gray-900">{TRANSLATIONS.featured[language]}</h2>
          <Link to="/categories" className="text-emerald-600 hover:text-emerald-700 font-medium flex items-center gap-1 text-sm md:text-base">
             {language === 'en' ? 'View All' : 'Zobacz Wszystkie'} <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6" role="list">
          {featuredProducts.map(product => (
            <ProductCard key={product.id} product={product} />
          ))}
        </div>
      </section>
    </main>
  );
};